import { useState, type ReactElement } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Avatar,
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
  Link,
  StackLayout,
  Text,
  VerticalNavigation,
  VerticalNavigationItem,
  VerticalNavigationItemContent,
  VerticalNavigationItemExpansionIcon,
  VerticalNavigationItemLabel,
  VerticalNavigationItemTrigger,
  VerticalNavigationSubMenu,
  SaltIcons,
} from "@/design-system/salt";
import type { NavItemData } from "./navigation-config";

export type SaltShellIcon = typeof SaltIcons.HomeIcon;

// ---------------------------------------------------------------------------
// Active-state helpers
// ---------------------------------------------------------------------------

function isItemActive(
  pathname: string,
  href: string,
  activePatterns?: readonly string[],
): boolean {
  if (pathname === href) return true;
  if (href !== "/" && pathname.startsWith(`${href}/`)) return true;
  return activePatterns?.some((p) => pathname.startsWith(p)) ?? false;
}

function isChildActive(
  pathname: string,
  children: readonly NavItemData[],
): boolean {
  return children.some(
    (child) =>
      isItemActive(pathname, child.path, child.activePatterns) ||
      (child.children && isChildActive(pathname, child.children)),
  );
}

// ---------------------------------------------------------------------------
// Recursive NestedItem — follows the Salt VerticalNavigation + Collapsible
// reference pattern exactly. All components from @salt-ds/core.
// ---------------------------------------------------------------------------

function NestedItem({
  item,
  onNavigate,
}: {
  item: NavItemData;
  onNavigate?: () => void;
}): ReactElement {
  const location = useLocation();

  // --- Parent with children ---
  if (Array.isArray(item.children) && item.children.length > 0) {
    return (
      <NestedItemWithChildren item={item} onNavigate={onNavigate} />
    );
  }

  // --- Leaf item ---
  return (
    <VerticalNavigationItem active={isItemActive(location.pathname, item.path, item.activePatterns)}>
      <VerticalNavigationItemContent>
        <VerticalNavigationItemTrigger render={<RouterLink to={item.path} onClick={onNavigate} />}>
          {item.icon ? <item.icon aria-hidden="true" /> : undefined}
          <VerticalNavigationItemLabel>
            {item.name}
          </VerticalNavigationItemLabel>
        </VerticalNavigationItemTrigger>
      </VerticalNavigationItemContent>
    </VerticalNavigationItem>
  );
}

function NestedItemWithChildren({
  item,
  onNavigate,
}: {
  item: NavItemData;
  onNavigate?: () => void;
}): ReactElement {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <VerticalNavigationItem
      active={isChildActive(location.pathname, item.children!) && collapsed}
    >
      <Collapsible onOpenChange={(_, expanded) => setCollapsed(!expanded)}>
        <VerticalNavigationItemContent>
          <CollapsibleTrigger>
            <VerticalNavigationItemTrigger>
              {item.icon ? <item.icon aria-hidden="true" /> : undefined}
              <VerticalNavigationItemLabel>
                {item.name}
              </VerticalNavigationItemLabel>
              <VerticalNavigationItemExpansionIcon />
            </VerticalNavigationItemTrigger>
          </CollapsibleTrigger>
        </VerticalNavigationItemContent>
        <CollapsiblePanel>
          <VerticalNavigationSubMenu>
            {item.children!.map((child) => (
              <NestedItem
                key={child.id}
                item={child}
                onNavigate={onNavigate}
              />
            ))}
          </VerticalNavigationSubMenu>
        </CollapsiblePanel>
      </Collapsible>
    </VerticalNavigationItem>
  );
}

export { NestedItem };

// ---------------------------------------------------------------------------
// Shell frame — all Salt, single flat VerticalNavigation
// ---------------------------------------------------------------------------

export function SaltShellNavigation({
  homePath,
  initials,
  onNavigate,
  onSignOut,
  subtitle,
  title,
  navItems,
}: {
  homePath: string;
  initials: string;
  onNavigate?: () => void;
  /**
   * Account exit lives in the shell after the chrome user-avatar menu was removed
   * app-wide. When set, Security settings + Sign out render under the nav list.
   */
  onSignOut?: () => void;
  subtitle?: string;
  title: string;
  navItems: readonly NavItemData[];
}) {
  return (
    <StackLayout
      direction="column"
      style={{
        minHeight: "100%",
        background: "var(--salt-container-primary-background)",
      }}
    >
      <StackLayout
        direction="row"
        align="center"
        gap={1}
        style={{
          borderBottom: "1px solid var(--salt-separable-primary-borderColor)",
          padding: "var(--salt-spacing-200)",
        }}
      >
        <Link
          aria-label={`Open ${title}`}
          href={homePath}
          onClick={onNavigate}
          style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "var(--salt-spacing-100)", minWidth: 0 }}
        >
          <Avatar
            name={title}
            nameToInitials={() => initials}
            size={1.5}
          />
          <StackLayout direction="column" gap={0} style={{ minWidth: 0 }}>
            <Text as="span" styleAs="h4" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {title}
            </Text>
            {subtitle ? (
              <Text as="span" styleAs="label" style={{ color: "var(--salt-content-secondary-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {subtitle}
              </Text>
            ) : null}
          </StackLayout>
        </Link>
      </StackLayout>

      <StackLayout
        direction="column"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "var(--salt-spacing-150)",
        }}
      >
        <VerticalNavigation
          aria-label="Application navigation"
          appearance="indicator"
          style={{ minWidth: "30ch" }}
        >
          {navItems.map((item) => (
            <NestedItem
              key={item.id}
              item={item}
              onNavigate={onNavigate}
            />
          ))}
        </VerticalNavigation>
      </StackLayout>

      {onSignOut ? (
        <StackLayout
          direction="column"
          gap={0}
          style={{
            borderTop: "1px solid var(--salt-separable-primary-borderColor)",
            padding: "var(--salt-spacing-150)",
          }}
        >
          <VerticalNavigation aria-label="Account" appearance="indicator">
            <VerticalNavigationItem>
              <VerticalNavigationItemContent>
                <VerticalNavigationItemTrigger
                  render={
                    <RouterLink to="/admin/settings/security" onClick={onNavigate} />
                  }
                >
                  <SaltIcons.LockedIcon aria-hidden="true" />
                  <VerticalNavigationItemLabel>Security settings</VerticalNavigationItemLabel>
                </VerticalNavigationItemTrigger>
              </VerticalNavigationItemContent>
            </VerticalNavigationItem>
            <VerticalNavigationItem>
              <VerticalNavigationItemContent>
                <VerticalNavigationItemTrigger
                  render={
                    <button
                      type="button"
                      onClick={() => {
                        onNavigate?.();
                        onSignOut();
                      }}
                    />
                  }
                >
                  <SaltIcons.TearOutIcon aria-hidden="true" />
                  <VerticalNavigationItemLabel>Sign out</VerticalNavigationItemLabel>
                </VerticalNavigationItemTrigger>
              </VerticalNavigationItemContent>
            </VerticalNavigationItem>
          </VerticalNavigation>
        </StackLayout>
      ) : null}
    </StackLayout>
  );
}
